import type { ReactNode } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { VirtualAppointmentListItem } from './VirtualAppointmentListItem';
import type { VirtualAppointmentSummary } from '@odo/shared-ui/clinical/virtualAppointments/summary';

export interface VirtualAppointmentListEntry {
    summary: VirtualAppointmentSummary;
    hasErrors: boolean;
    removable: boolean;
    /** A planned VA the trigger would open now. */
    due?: boolean;
}

interface AddActionProps {
    onAdd: () => void;
    canAdd: boolean;
    disabledHint?: string;
}

function AddFooter({ onAdd, canAdd, disabledHint }: AddActionProps) {
    return (
        <div className="flex-shrink-0 border-t border-gray-200 p-3">
            <button
                type="button"
                onClick={onAdd}
                disabled={!canAdd}
                title={canAdd ? undefined : disabledHint}
                className="w-full flex items-center justify-center gap-1.5 bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
                <PlusIcon className="w-4 h-4" />
                Add VA
            </button>
        </div>
    );
}

interface VirtualAppointmentSelectorListProps {
    entries: VirtualAppointmentListEntry[];
    selectedKey: string | null;
    onSelect: (key: string) => void;
    /** Shown only on entries marked removable. */
    onRemove?: (key: string) => void;
    /** Omit for read-only surfaces that cannot create VAs. */
    add?: AddActionProps;
    /** Pinned under the rows (above the add action, if any), e.g. a summary line. */
    footer?: ReactNode;
    emptyText?: string;
}

// The list panel of the VA split: rows scroll on their own above a pinned add footer.
export function VirtualAppointmentSelectorList({
    entries,
    selectedKey,
    onSelect,
    onRemove,
    add,
    footer,
    emptyText = 'No VAs yet.'
}: VirtualAppointmentSelectorListProps) {
    return (
        <>
            {entries.length === 0 ? (
                <div className="flex-1 min-h-0 flex items-center justify-center px-4 py-6">
                    <p className="text-center text-sm text-gray-500">{emptyText}</p>
                </div>
            ) : (
                <nav
                    aria-label="VAs"
                    className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-0.5 p-2"
                >
                    {entries.map(({ summary, hasErrors, removable, due }) => (
                        <VirtualAppointmentListItem
                            key={summary.key}
                            summary={summary}
                            selected={summary.key === selectedKey}
                            hasErrors={hasErrors}
                            due={due}
                            onSelect={onSelect}
                            onRemove={removable ? onRemove : undefined}
                        />
                    ))}
                </nav>
            )}
            {footer && (
                <div className="flex-shrink-0 border-t border-gray-200 px-3 py-2.5">{footer}</div>
            )}
            {add && <AddFooter {...add} />}
        </>
    );
}
